import axios, { AxiosProgressEvent } from "axios";
import { config } from "../config";
import instance from "./instance";
import { getToken, rearrangeArrayById } from "../helpers/util";

export async function getListingData(
  cancelToken: any,
  mapCenter: any,
  mapZoom: any,
  search?: string,
  type?: string
) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: `${config.apiUrl}get-listings?latitude=${mapCenter.lat}&longitude=${
        mapCenter.lng
      }&zoom=${mapZoom}${search ? "&search=" + search : ""}${
        type ? "&" + type : ""
      }`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      cancelToken: cancelToken,
    });
  } else {
    return axios({
      method: "get",
      url: `${config.apiUrl}get-listings?latitude=${mapCenter.lat}&longitude=${mapCenter.lng}&zoom=${mapZoom}`,
      cancelToken: cancelToken,
    });
  }
}

export async function getListingDataByListingUniqueId(
  params: any,
  cancelToken: any
) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "get-listing-details",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
      cancelToken: cancelToken,
    });
  } else {
    return axios({
      method: "post",
      url: config.apiUrl + "get-listing-details",
      data: params,
      cancelToken: cancelToken,
    });
  }
}

export async function callLogout() {
  return axios({
    method: "get",
    url: config.apiUrl + "logout",
  });
}

export async function login(params: any) {
  return axios({
    method: "post",
    url: config.apiUrl + "login",
    headers: { "Content-Type": "application/json" },
    data: params,
  });
}

export async function register(params: any) {
  return axios({
    method: "post",
    url: config.apiUrl + "register",
    headers: { "Content-Type": "application/json" },
    data: params,
  });
}

export async function verifyToken(token: string | null) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "verify-token",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: {
        api_token: token,
      },
    });
  } else {
    await Promise.reject("Unauthorized");
  }
}

export async function insertListing(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "insert-listing",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function updateListing(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "update-listing",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function updateListingLeadStatus(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "update-listing-lead-status",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function importListing(
  formData: any,
  progressEvent: (e: AxiosProgressEvent) => void
) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "import-listing",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      data: formData,
      onUploadProgress: progressEvent,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function uploadAttachment(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "upload-attachment",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getListingAttachmentData(listingID: number) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "get",
      url:
        config.apiUrl +
        "get-listing-attachments?listing_unique_id=" +
        listingID,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
    });
  } else {
    return axios({
      method: "get",
      url:
        config.apiUrl +
        "get-listing-attachments?listing_unique_id=" +
        listingID,
    });
  }
}

export async function getLeadStatusOptions() {
  const api = await axios({
    method: "get",
    url: config.apiUrl + "get-lead-status-options",
  });

  if (api?.data?.data) {
    const order = [1, 3, 2, 6, 4, 13, 5, 12, 9, 10, 11];
    const data = rearrangeArrayById(api.data.data, order);
    return { ...api, data: { data } };
  }
  return api;
}

export async function getListingLeadStatusData(listingID: number) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url:
        config.apiUrl +
        "get-listing-lead-statuses?listing_unique_id=" +
        listingID,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
    });
  } else {
    return axios({
      method: "post",
      url:
        config.apiUrl +
        "get-listing-lead-statuses?listing_unique_id=" +
        listingID,
    });
  }
}

export async function getUsers(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "get-users",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getMyListings(
  formData: any,
  currentPage = 1,
  pageSize = 10
) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: `${config.apiUrl}get-my-listings?per_page=${pageSize}&page=${currentPage}`,
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function addLeadNote(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "add-listing-lead-note",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getListingLeadNotesData(listingID: number) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url:
        config.apiUrl + "get-listing-lead-notes?listing_unique_id=" + listingID,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
    });
  } else {
    return axios({
      method: "get",
      url:
        config.apiUrl + "get-listing-lead-notes?listing_unique_id=" + listingID,
    });
  }
}

export async function addToFavorites(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "add-to-favorites",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function removeFromFavorites(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "remove-from-favorites",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getUserFavorites() {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "get-favorites",
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function addReminder(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "add-reminder",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function removeReminder(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "remove-reminder",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getUserReminders() {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "get-reminders",
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function markReminderRead(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "mark-reminder-read",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function addRoute(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "add-route",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function editRoute(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "update-route",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function removeRoute(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "remove-route",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getUserRoutes() {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "get-routes",
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function inviteToTeam(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "invite-team",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function changeProfilePicture(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "change-profile-picture",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function updateProfile(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "update-profile",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getTeamActivityData(currentPage = 1, pageSize = 10) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: `${config.apiUrl}get-team-activity?per_page=${pageSize}&page=${currentPage}`,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getBatchData(formData: any) {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "get-batch-data",
      data: formData,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getTeamFeatures() {
  const apiToken = getToken();
  if (apiToken) {
    return axios({
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${apiToken}`,
      },
      url: config.apiUrl + "get-team-features",
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function deleteListing(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "delete-listing",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function assignArea(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "add-area",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function deleteArea(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "remove-area",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function editArea(params: any, id: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: config.apiUrl + "edit-area/" + id,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getPrint(routeId: any, signal: AbortSignal) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: `${config.apiUrl}get-route-pdf?route_id=${routeId}`,
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
        Accept: "application/json",
      },
      responseType: "blob",
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function saveUserFilter(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: `${config.apiUrl}update-filters`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
        Accept: "application/json",
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function getCSV(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: `${config.apiUrl}export-my-listings`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
        Accept: "application/json",
      },
      data: params,
      responseType: "blob",
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function sentMailRequest(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: `${config.apiUrl}generate-post-mail-letter`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
        Accept: "application/json",
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}

export async function cancelMailRequest(params: any) {
  const apiToken = getToken();
  if (apiToken) {
    return instance({
      method: "post",
      url: `${config.apiUrl}cancel-post-mail`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiToken}`,
        Accept: "application/json",
      },
      data: params,
    });
  } else {
    Promise.reject("Unauthorized");
  }
}
